// /portal/notifications — persistent feed (Phase G).
//
// Fetches from GET /api/notifications. Mark-on-view: when the page loads
// and there are unread rows, fire POST /mark-read { all: true } so the nav
// badge clears on next refresh. Each row is dismissible via the × button.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { NotificationDto, NotificationKind } from '@shared';
import { apiDelete, apiGet, apiPost, ApiError } from '../../lib/api';

const KIND_TONE: Record<NotificationKind, string> = {
  proposal_sent: 'amber',
  proposal_accepted: 'green',
  proposal_declined: 'red',
  invoice_issued: 'amber',
  invoice_paid: 'green',
  payment_refunded: 'red',
  document_published: 'accent',
  request_status_changed: 'accent',
  // "Omega replied on your support ticket" — matches the support sidebar
  // glyph ('?') and the "new content from Omega" feel; not amber/red.
  support_ticket_replied: 'accent',
  lead_changed: 'accent',
  contact_invited: 'accent',
  contact_activated: 'green',
  // portal-kyc kinds — Phase A registers them so types compile; Phase F
  // wires the actual notification rows + may revisit these tones.
  kyc_submission_received: 'accent',
  kyc_doc_accepted: 'green',
  kyc_doc_rejected: 'amber',
  kyc_finalised: 'green',
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86_400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 7 * 86_400) return `${Math.round(diff / 86_400)}d ago`;
  return iso.slice(0, 10);
}

type Filter = 'all' | 'unread';

export function PortalNotifications() {
  const [rows, setRows] = useState<NotificationDto[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiGet<{ notifications: NotificationDto[] }>('/api/notifications');
      setRows(r.notifications);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Could not load notifications.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Mark all unread as read once on view. The unread badge clears on the
  // next layout poll (60s) — the user gets the visual confirmation that
  // they've seen the items.
  useEffect(() => {
    if (rows === null) return;
    if (rows.some((r) => r.read_at === null)) {
      apiPost('/api/notifications/mark-read', { all: true })
        .then(() => load())
        .catch(() => { /* silent */ });
    }
    // We deliberately depend on `rows` only, not `load`, so a re-fetch
    // doesn't re-trigger mark-read in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  async function dismiss(id: string) {
    setRows((prev) => prev?.filter((r) => r.id !== id) ?? prev);
    try {
      await apiDelete(`/api/notifications/${id}`);
    } catch {
      // Roll back on failure.
      void load();
    }
  }

  const list = useMemo(
    () => (rows ?? []).filter((r) => (filter === 'unread' ? r.read_at === null : true)),
    [rows, filter],
  );
  const unreadCount = (rows ?? []).filter((r) => r.read_at === null).length;

  return (
    <>
      <div>
        <h1 className="portal-h1">Notifications</h1>
        <p className="portal-sub">
          Every proposal, invoice, payment and document event in one feed. Items mark themselves
          read on view; use × to dismiss.
        </p>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="tabs" style={{ marginTop: 16 }}>
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>
          All {rows !== null && <span className="tab-count">{rows.length}</span>}
        </button>
        <button className={filter === 'unread' ? 'on' : ''} onClick={() => setFilter('unread')}>
          Unread {unreadCount > 0 && <span className="tab-count">{unreadCount}</span>}
        </button>
      </div>

      {rows !== null && list.length === 0 && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', color: 'var(--fg-mute)' }}>
          {filter === 'unread' ? 'You\'re all caught up.' : 'No notifications yet.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        {list.map((n) => {
          const isUnread = n.read_at === null;
          const card = (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                padding: 16,
                background: isUnread ? 'var(--bg-soft)' : undefined,
                border: '1px solid var(--rule)',
                borderRadius: 'var(--r)',
                position: 'relative',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 4 }}>
                  <span className={`chip ${KIND_TONE[n.kind] ?? 'neutral'}`} style={{ fontSize: 11 }}>
                    {n.kind.replace(/_/g, ' ')}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {relativeTime(n.created_at)}
                  </span>
                </div>
                <div style={{ fontWeight: isUnread ? 600 : 500, fontSize: 14.5 }}>{n.title}</div>
                {n.body && (
                  <div style={{ color: 'var(--fg-soft)', fontSize: 13, marginTop: 4 }}>{n.body}</div>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); void dismiss(n.id); }}
                aria-label="Dismiss"
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'var(--fg-mute)',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: '4px 8px',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          );
          return n.href
            ? <Link key={n.id} to={n.href} style={{ textDecoration: 'none', color: 'inherit' }}>{card}</Link>
            : <div key={n.id}>{card}</div>;
        })}
      </div>
    </>
  );
}
